import React, { ComponentType, FC, memo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import Analytics from '@analytics/index';
import PageLoader from 'Components/loader/pageLoader';
import { areEqualObjects } from 'Utils/lodashUtils';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '@store/root/rootState';
import { GeneralError } from 'Components/helpers';
import { sha512DigestHex } from '@src/utils/ie11SafeCrypto';
import { StoreUserIdentifier } from '@src/analyticsManager';
import { v4 } from 'uuid';
import { formQuery } from './gqlQuery';

export default function withFormData(WrappedComponent: ComponentType) {
  const Tour: FC<any> = memo((props: any) => {
    const dispatch = useDispatch();
    const tourModel = useSelector((state: IRootState) => state.tour);
    const analyticsModel = useSelector((state: IRootState) => state.analytics);
    // Fetch the initial data
    const { loading, error, data } = useQuery(formQuery, {
      variables: {
        instId: tourModel.instId,
        locId: tourModel.locId,
        source: analyticsModel.analytics.source,
        originalSource: analyticsModel.analytics.originalSource,
        userkey: analyticsModel.analytics.userkey,
        sessionid: analyticsModel.analytics.sessionId,
      },
    });

    const { institution_name: institutionName, location_name: locationName } =
      data?.formData ?? {};

    useEffect(() => {
      // to identify user in tracking services (Gainsight) used with AnalyticsManager
      if (analyticsModel.isReady && !loading && !error) {
        const sourceType = tourModel.fromSearch
          ? 'college search'
          : 'client site';

        const userkey = analyticsModel.analytics.userkey
          ? analyticsModel.analytics.userkey
          : v4();

        sha512DigestHex(userkey).then(userkeyHex => {
          dispatch(
            StoreUserIdentifier({
              id: userkeyHex,
              sourceType,
              institution_id: tourModel.instId,
              institution_name: institutionName,
              location_id: tourModel.locId,
              location_name: locationName,
            })
          );
        });
      }
    }, [
      dispatch,
      analyticsModel.analytics.userkey,
      analyticsModel.isReady,
      tourModel.fromSearch,
      tourModel.instId,
      tourModel.locId,
      institutionName,
      locationName,
      loading,
      error,
    ]);

    if (loading) {
      return <PageLoader />;
    }
    if (error) {
      // Default analytics data
      // Use URI query params in case it is malformed and can't make call for api-based data

      const defaultData = {
        ...tourModel,
        container_type: 'conversational inquiry form',
      };

      // Put error state analytics event
      Analytics.EventAnalytics.track('registration_error_loading', defaultData);

      // close
      // Post message to parent iframe to close on completion
      setTimeout(() => {
        window.parent.postMessage('RegistrationForm_Completed', '*');
      }, 500);

      // Return error notification.  Will only be displayed briefly before closing
      return (
        <GeneralError title="Error Loading Config" message={error.message} />
      );
    }
    return <WrappedComponent data={data.formData} {...props} />;

    // Add the tour
  }, areEqualObjects);
  return Tour;
}
try {
    // @ts-ignore
    WithFormData.displayName = "WithFormData";
    // @ts-ignore
    WithFormData.__docgenInfo = { "description": "", "displayName": "WithFormData", "props": { "propTypes": { "defaultValue": null, "description": "", "name": "propTypes", "required": false, "type": { "name": "WeakValidationMap<{}>" } }, "contextType": { "defaultValue": null, "description": "", "name": "contextType", "required": false, "type": { "name": "Context<any>" } }, "contextTypes": { "defaultValue": null, "description": "", "name": "contextTypes", "required": false, "type": { "name": "ValidationMap<any>" } }, "childContextTypes": { "defaultValue": null, "description": "", "name": "childContextTypes", "required": false, "type": { "name": "ValidationMap<any>" } }, "defaultProps": { "defaultValue": null, "description": "", "name": "defaultProps", "required": false, "type": { "name": "Partial<{}>" } }, "displayName": { "defaultValue": null, "description": "", "name": "displayName", "required": false, "type": { "name": "string" } }, "getDerivedStateFromProps": { "defaultValue": null, "description": "", "name": "getDerivedStateFromProps", "required": false, "type": { "name": "GetDerivedStateFromProps<{}, any>" } }, "getDerivedStateFromError": { "defaultValue": null, "description": "", "name": "getDerivedStateFromError", "required": false, "type": { "name": "GetDerivedStateFromError<{}, any>" } }, "apply": { "defaultValue": null, "description": "Calls the function, substituting the specified object for the this value of the function, and the specified array for the arguments of the function.\n@param thisArg The object to be used as the this object.\n@param argArray A set of arguments to be passed to the function.", "name": "apply", "required": true, "type": { "name": "(this: Function, thisArg: any, argArray?: any) => any" } }, "call": { "defaultValue": null, "description": "Calls a method of an object, substituting another object for the current object.\n@param thisArg The object to be used as the current object.\n@param argArray A list of arguments to be passed to the method.", "name": "call", "required": true, "type": { "name": "(this: Function, thisArg: any, ...argArray: any[]) => any" } }, "bind": { "defaultValue": null, "description": "For a given function, creates a bound function that has the same body as the original function.\nThe this object of the bound function is associated with the specified object, and has the specified initial parameters.\n@param thisArg An object to which the this keyword can refer inside the new function.\n@param argArray A list of arguments to be passed to the new function.", "name": "bind", "required": true, "type": { "name": "(this: Function, thisArg: any, ...argArray: any[]) => any" } }, "toString": { "defaultValue": null, "description": "Returns a string representation of a function.", "name": "toString", "required": false, "type": { "name": "() => string" } }, "prototype": { "defaultValue": null, "description": "", "name": "prototype", "required": true, "type": { "name": "any" } }, "length": { "defaultValue": null, "description": "", "name": "length", "required": true, "type": { "name": "number" } }, "arguments": { "defaultValue": null, "description": "", "name": "arguments", "required": true, "type": { "name": "any" } }, "caller": { "defaultValue": null, "description": "", "name": "caller", "required": true, "type": { "name": "Function" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/hoc/WithFormData/WithFormData.tsx#WithFormData"] = { docgenInfo: WithFormData.__docgenInfo, name: "WithFormData", path: "src/hoc/WithFormData/WithFormData.tsx#WithFormData" };
}
catch (__react_docgen_typescript_loader_error) { }